import { motion } from 'framer-motion'
import { TrendingUp, Target, Percent, Clock, Award, AlertTriangle } from 'lucide-react'
import { formatCurrencyCompact } from '@/utils/format-currency'
import { cn } from '@/utils/cn'
import type { Deal } from '@/types'

function avgDaysToClose(deals: Deal[]): number {
  const closedDeals = deals.filter((d) => ['ganho', 'perdido'].includes(d.etapa))
  if (closedDeals.length === 0) return 0
  const totalDays = closedDeals.reduce((s, d) => {
    const created = new Date(d.createdAt).getTime()
    const updated = new Date(d.updatedAt).getTime()
    return s + Math.ceil((updated - created) / (1000 * 60 * 60 * 24))
  }, 0)
  return Math.round(totalDays / closedDeals.length)
}

interface MetricsCardsProps {
  deals: Deal[]
}

export function MetricsCards({ deals }: MetricsCardsProps) {
  const activeDeals = deals.filter((d) => !['ganho', 'perdido'].includes(d.etapa))
  const wonDeals = deals.filter((d) => d.etapa === 'ganho')
  const stagnantDeals = deals.filter((d) => d.stagnant)
  const pipelineTotal = activeDeals.reduce((s, d) => s + d.valor, 0)
  const forecastTotal = pipelineTotal * 0.95
  const avgProbability = activeDeals.length
    ? Math.round(activeDeals.reduce((s, d) => s + d.probabilidade, 0) / activeDeals.length)
    : 0
  const wonTotal = wonDeals.reduce((s, d) => s + d.valor, 0)

  const metrics = [
    {
      label: 'Pipeline Total',
      value: formatCurrencyCompact(pipelineTotal),
      sublabel: `${activeDeals.length} deals ativos`,
      icon: TrendingUp,
      color: 'text-accent',
      bgColor: 'bg-accent/10',
    },
    {
      label: 'Forecast',
      value: formatCurrencyCompact(forecastTotal),
      sublabel: 'Ponderado por probabilidade',
      icon: Target,
      color: 'text-emerald-400',
      bgColor: 'bg-emerald-500/10',
    },
    {
      label: 'Probabilidade Média',
      value: `${avgProbability}%`,
      sublabel: 'Dos deals ativos',
      icon: Percent,
      color: 'text-violet-400',
      bgColor: 'bg-violet-500/10',
    },
    {
      label: 'Ganhos no Período',
      value: formatCurrencyCompact(wonTotal),
      sublabel: `${wonDeals.length} deals fechados`,
      icon: Award,
      color: 'text-amber-400',
      bgColor: 'bg-amber-500/10',
    },
    {
      label: 'Deals Stagnants',
      value: String(stagnantDeals.length),
      sublabel: 'Sem movimentação',
      icon: AlertTriangle,
      color: 'text-red-400',
      bgColor: 'bg-red-500/10',
    },
    {
      label: 'Tempo Médio',
      value: `${avgDaysToClose(deals)}d`,
      sublabel: 'Para fechamento',
      icon: Clock,
      color: 'text-sky-400',
      bgColor: 'bg-sky-500/10',
    },
  ]

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
      {metrics.map((metric, i) => (
        <motion.div
          key={metric.label}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.05, duration: 0.3 }}
          className="glass-card p-4 space-y-2"
        >
          <div className="flex items-center justify-between">
            <span
              className="text-text-label uppercase tracking-wider font-medium"
              style={{ fontSize: 'var(--font-size-label)' }}
            >
              {metric.label}
            </span>
            <div className={cn('w-7 h-7 rounded-lg flex items-center justify-center', metric.bgColor)}>
              <metric.icon size={14} className={metric.color} />
            </div>
          </div>
          <p className={cn('text-xl font-bold text-text-primary')}>{metric.value}</p>
          <p className="text-text-muted" style={{ fontSize: 'var(--font-size-xs)' }}>
            {metric.sublabel}
          </p>
        </motion.div>
      ))}
    </div>
  )
}
